import { useSnapshot } from "valtio";
import React, { FC, useState } from "react";
import { Image, Flex, Text, Button, useToast } from "@chakra-ui/react";

import PaymentIcon from "../../../assets/payment.svg";
import { mailCreditsStore, userStore } from "../../../hooks/store";
import {
  createDefaultInAppPackage,
  InAppPackage,
} from "../../../types/mailcredit";
import { formatNumberMax } from "../../../utils/format.number";
import {
  activePaymentClient,
  activePaymentConfig,
} from "../../../utils/payment";
import { buyInAppPackage } from "../../../apis/mail-credits/buy.in.app.package";
import { verifyTransaction } from "../../../apis/transactions/verify.transaction";
import {
  Transaction,
  TransactionCtx,
  TransactionStatus,
} from "../../../types/transaction";
import { getAllInitiatorsTransactions } from "../../../apis/transactions/get.all.initiators.transactions";
import { editorState } from "../../../utils/editor";

type CloseBtnProps = {
  onClose: () => void;
};

const CloseBtn: FC<CloseBtnProps> = ({ onClose }) => {
  return (
    <Flex
      justifyContent={"center"}
      alignItems={"center"}
      w={"48px"}
      h={"48px"}
      bgColor={"#F9F9F9"}
      borderRadius={"50px"}
      onClick={onClose}
      cursor={"pointer"}
    >
      <svg
        width="16"
        height="15"
        viewBox="0 0 16 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <line
          x1="15.1036"
          y1="0.603553"
          x2="1.60355"
          y2="14.1036"
          stroke="black"
        />
        <line
          y1="-0.5"
          x2="19.0919"
          y2="-0.5"
          transform="matrix(0.707107 0.707107 0.707107 -0.707107 1.25 0.25)"
          stroke="black"
        />
      </svg>
    </Flex>
  );
};

type CreditBalanceCardProps = {
  balance: number;
  onHistoryClick: () => void;
};

const CreditBalanceCard: FC<CreditBalanceCardProps> = ({
  balance,
  onHistoryClick,
}) => {
  return (
    <Flex
      mt={"15px"}
      w={"full"}
      py={"2rem"}
      flexDir={"column"}
      justify={"center"}
      textAlign={"center"}
      alignItems={"center"}
      bgColor={"#F4FFF2"}
      borderRadius={"32px"}
      fontFamily={"font4"}
      justifyContent={"center"}
    >
      <Image
        src={PaymentIcon}
        height={{ base: "3.5rem", md: "4rem" }}
        w={"3.5rem"}
      />

      <Flex
        alignItems={"baseline"}
        fontSize={{ base: "22px", md: "28px" }}
        gap={2}
        mt={"10px"}
      >
        <Text color={"#000000"}>{formatNumberMax(balance)}</Text>
        <Text color={"#595858"}>mails left</Text>
      </Flex>

      <Flex
        mt={"1rem"}
        w={"fit-content"}
        borderRadius={0}
        cursor={"pointer"}
        onClick={onHistoryClick}
        color="#4C9063"
        fontSize={"1rem"}
        fontWeight={500}
        bg={"transparent"}
        _hover={{ bg: "transparent" }}
        pb={"0.05rem"}
        borderBottom="1.5px solid #4C9063"
        textTransform={"uppercase"}
        letterSpacing={"1.25px"}
      >
        History
      </Flex>
    </Flex>
  );
};

type CreditsPackProps = {
  inAppPackage: InAppPackage;
  isSelected: boolean;
  onSelect: () => void;
};
const CreditsPack: FC<CreditsPackProps> = ({
  inAppPackage,
  isSelected,
  onSelect,
}) => {
  return (
    <Flex
      py={"18px"}
      px={"22px"}
      h={"90px"}
      w={"full"}
      borderRadius={"32px"}
      border={isSelected ? "1.5px solid #58FF90" : "4px solid #F9F9F9"}
      justifyContent={"space-between"}
      onClick={onSelect}
      cursor={"pointer"}
    >
      <Flex direction="column">
        <Text fontSize={"0.78rem"} color="#000000" textTransform={"uppercase"}>
          {inAppPackage.name} &nbsp;pack
        </Text>
        <Text fontSize="20px" color="#000000">
          {inAppPackage.credits} mails
        </Text>
      </Flex>
      <Text fontSize="20px" color="#000000">
        ₦{inAppPackage.amount.toLocaleString()}
      </Text>
    </Flex>
  );
};

const txStatusBgColors: Record<TransactionStatus, string> = {
  [TransactionStatus.TxStatusSuccess]: "#F4FFF2",
  [TransactionStatus.TxStatusPending]: "#e0ac2b4F",
  [TransactionStatus.TxStatusFailed]: "#BB27274F",
};

type PayButtonProps = {
  inAppPackage: InAppPackage;
  isLoading: boolean;
  onClick: () => void;
};
const PayButton: FC<PayButtonProps> = ({
  inAppPackage,
  isLoading,
  onClick,
}) => {
  return (
    <Button
      w={"full"}
      h={"60px"}
      mt={"26px"}
      display={"flex"}
      alignItems={"center"}
      justifyContent={"center"}
      bgColor={"#4C9063"}
      color={"#FFFFFF"}
      borderRadius={"50px"}
      isDisabled={!inAppPackage._id}
      onClick={onClick}
      isLoading={isLoading}
    >
      <Text
        fontSize={"16px"}
        fontWeight={600}
        fontFamily={"font4"}
        textTransform={"uppercase"}
      >
        {inAppPackage._id
          ? `Pay ₦${inAppPackage.amount.toLocaleString()} for ${
              inAppPackage.name
            }`
          : "Select a package"}
      </Text>
    </Button>
  );
};

type ExistingTransactionProps = {
  transaction: Transaction;
  inAppPackage: InAppPackage;
};
const ExistingTransaction: FC<ExistingTransactionProps> = ({
  transaction,
  inAppPackage,
}) => {
  return (
    <Flex
      bgColor={txStatusBgColors[transaction.transactionStatus]}
      border={"3px solid #F9F9F9"}
      py={"18px"}
      px={"22px"}
      h={"90px"}
      w={"full"}
      borderRadius={"32px"}
      justifyContent={"space-between"}
    >
      <Flex direction={"column"}>
        <Text fontSize={"16px"} color="#595858">
          {inAppPackage.name}
        </Text>
        <Text color="#595858" fontSize="18px" fontWeight="bold">
          {inAppPackage.credits} mails
        </Text>
      </Flex>

      <Flex direction={"column"}>
        <Text color="#595858" fontSize="18px" fontWeight="bold">
          ₦{inAppPackage.amount.toLocaleString()}
        </Text>
        <Text fontSize="16px" color="#595858">
          {new Date(transaction.createdAt).toLocaleDateString()}
        </Text>
      </Flex>
    </Flex>
  );
};

const getInAppPackageByID = (
  packageById: string,
  inAppPackages: InAppPackage[],
) => {
  return inAppPackages.find((pkg) => pkg._id === packageById);
};

type PaymentDrawerProps = {
  onClose: () => void;
  onPurchaseVerified?: () => void;
};
const PaymentDrawer: React.FC<PaymentDrawerProps> = ({
  onClose,
  onPurchaseVerified,
}) => {
  const { XAccountHash } = useSnapshot(editorState);
  const [selectedPackage, setSelectedPackage] = useState(
    createDefaultInAppPackage(),
  );
  const { inAppPackages, inAppCredits, transactionList } =
    useSnapshot(mailCreditsStore);
  const {
    data: { email },
  } = useSnapshot(userStore);
  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState<"payment" | "history">("payment");

  const toast = useToast({
    position: "top",
    containerStyle: { zIndex: 9 },
  });

  const initializePayment = activePaymentConfig.usePaymentHook({
    ...activePaymentConfig,
    email,
  });

  const goToHistory = () => {
    setView("history");
    getAllInitiatorsTransactions({
      headers: {
        "X-Account-Hash": XAccountHash || "",
      },
      query: {
        page: 1,
        transactionCtx: TransactionCtx.TxCtxBuyPackage,
      },
      onCompleted(params) {
        mailCreditsStore.transactionCount = params.data.transactionCount;
        mailCreditsStore.transactionList = params.data.transactionList;
      },
      onError(err) {
        console.error(err);
      },
    });
  };

  return (
    <>
      {view === "payment" && (
        <Flex
          p={{ base: "", md: "24px" }}
          pb={{ base: "10px", md: "30px" }}
          fontFamily={"font4"}
          h={"fit-content"}
          flexDir={"column"}
          overflowY={"scroll"}
          className={"no-scrollbar"}
        >
          <Flex
            w={"full"}
            justifyContent={"space-between"}
            alignItems={"center"}
            mt={{ base: "24px", md: "0px" }}
            mb={"10px"}
          >
            <Text
              fontFamily={"font4"}
              fontWeight={400}
              fontSize={"24px"}
              lineHeight={"36.32px"}
              textTransform={"uppercase"}
              letterSpacing={"1.25px"}
            >
              Buy Package
            </Text>
            <CloseBtn onClose={onClose} />
          </Flex>

          <CreditBalanceCard
            balance={inAppCredits.balance}
            onHistoryClick={goToHistory}
          />

          <Flex
            direction="column"
            mt="33px"
            w="100%"
            alignItems="center"
            gap={"24px"}
          >
            {inAppPackages.map((inAppPackage, name) => (
              <CreditsPack
                key={name + "pack"}
                inAppPackage={inAppPackage}
                isSelected={inAppPackage._id === selectedPackage._id}
                onSelect={() => {
                  setSelectedPackage(inAppPackage);
                }}
              />
            ))}
          </Flex>

          <PayButton
            inAppPackage={selectedPackage}
            isLoading={isLoading}
            onClick={() => {
              if (isLoading) return;
              setIsLoading(true);
              buyInAppPackage({
                headers: {
                  "X-Account-Hash": XAccountHash,
                },
                body: {
                  iapID: selectedPackage._id,
                  paymentClient: activePaymentClient,
                },
                onCompleted(params) {
                  initializePayment({
                    config: {
                      email,
                      reference: params.data.transactionReference,
                      amount: params.data.amount * 100,
                    },
                    onSuccess: (res: any) => {
                      setIsLoading(false);
                      toast({
                        status: "loading",
                        description: `${selectedPackage.credits} mails purchase processing`,
                      });
                      verifyTransaction({
                        headers: {
                          "X-Account-Hash": XAccountHash || "",
                        },
                        body: {
                          transactionReference:
                            params.data.transactionReference,
                          paymentClient: activePaymentClient,
                        },
                        onCompleted() {
                          onPurchaseVerified?.();
                        },
                        onError(err) {
                          console.error(err);
                        },
                      });
                      goToHistory();
                      // console.log({ res });
                    },
                    onClose: () => {
                      setIsLoading(false);
                      console.log("closed");
                    },
                  });
                },
                onError(err) {
                  console.error(err);
                  toast({
                    status: "error",
                    description: `${selectedPackage.credits} mails purchase failed`,
                  });
                  setIsLoading(false);
                },
              });
            }}
          />
        </Flex>
      )}
      {view === "history" && (
        <Flex
          p={{ base: "", md: "24px" }}
          pb={{ base: "10px", md: "30px" }}
          alignItems={"center"}
          flexDirection={"column"}
          fontFamily={"font4"}
        >
          <Button
            mb="20px"
            color="#4C9063"
            fontWeight="normal"
            bg={"transparent"}
            borderRadius="0px"
            _hover={{ bg: "transparent" }}
            borderBottom="1px solid #4C9063"
            onClick={() => setView("payment")}
          >
            Back to Payment
          </Button>

          {transactionList.length === 0 ? (
            <Text>No purchase history available.</Text>
          ) : (
            <Flex direction="column" align="center" gap="20px" w={"full"}>
              {transactionList.map((transaction, index) => (
                <ExistingTransaction
                  key={index + "str"}
                  transaction={transaction}
                  inAppPackage={
                    getInAppPackageByID(
                      transaction.transactionCtxID,
                      inAppPackages as InAppPackage[],
                    )!
                  }
                />
              ))}
            </Flex>
          )}
        </Flex>
      )}
    </>
  );
};

export default PaymentDrawer;
